<script lang="ts">
  /**
   * One question, two answers: a title, a sentence of consequence, the action
   * (danger or primary) and the safe way out.
   *
   * Every way of dismissing it without choosing — Escape, Back, the backdrop —
   * is the safe answer, and the safe button is where focus lands: a stray
   * Enter must never be the destructive choice.
   *
   * `children` (since 1.2.0) goes between the sentence and the buttons: a
   * `Field` to type a name into before a deletion, a line of facts. With
   * `confirmDisabled` the action waits until what is typed matches. Focus
   * still lands on the safe button, unless the content marks an element
   * `[data-autofocus]` (a `Field`'s `autofocus`): it comes first, so it wins.
   *
   * Test ids: `testids.action` (default `ui-confirm-action`) and
   * `testids.safe` (default `ui-confirm-safe`); `testid` is the dialog's.
   */
  import type { Snippet } from 'svelte';

  import Sheet from './Sheet.svelte';

  interface Props {
    readonly open: boolean;
    readonly title: string;
    readonly body: string;
    readonly confirmLabel: string;
    readonly cancelLabel: string;
    /** `danger` paints the action in the danger colour; `primary` in the accent. */
    readonly tone?: 'danger' | 'primary';
    readonly onconfirm: () => void;
    readonly oncancel: () => void;
    /** Between the sentence and the buttons: a field to type into, a line of facts. */
    readonly children?: Snippet | undefined;
    /** Holds the action back (until what is typed matches). */
    readonly confirmDisabled?: boolean;
    readonly testid?: string | undefined;
    readonly testids?: { readonly action?: string; readonly safe?: string } | undefined;
  }
  const {
    open,
    title,
    body,
    confirmLabel,
    cancelLabel,
    tone = 'primary',
    onconfirm,
    oncancel,
    children,
    confirmDisabled = false,
    testid,
    testids,
  }: Props = $props();

  const bodyId = $props.id();
</script>

<Sheet
  {open}
  {title}
  onclose={oncancel}
  variant="dialog"
  role="alertdialog"
  describedby={bodyId}
  {testid}
>
  <p id={bodyId} class="ui-confirm-body">{body}</p>
  {#if children !== undefined}
    <div class="ui-confirm-extra">{@render children()}</div>
  {/if}
  <div class="ui-confirm-actions">
    <button
      type="button"
      class="ui-confirm-action"
      class:ui-confirm-action--danger={tone === 'danger'}
      data-testid={testids?.action ?? 'ui-confirm-action'}
      disabled={confirmDisabled}
      onclick={() => {
        if (!confirmDisabled) onconfirm();
      }}
    >
      {confirmLabel}
    </button>
    <button
      type="button"
      class="ui-confirm-safe"
      data-testid={testids?.safe ?? 'ui-confirm-safe'}
      data-autofocus
      onclick={oncancel}
    >
      {cancelLabel}
    </button>
  </div>
</Sheet>

<style>
  .ui-confirm-body {
    margin: 0 0 var(--ui-space-5);
    font: var(--ui-text-body);
    font-weight: 700;
    line-height: 1.45;
    color: var(--ui-ink-2);
  }

  .ui-confirm-extra {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-3);
    margin: 0 0 var(--ui-space-5);
  }

  .ui-confirm-actions {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
  }

  .ui-confirm-action,
  .ui-confirm-safe {
    min-height: 2.9rem;
    border-radius: 999px;
    padding: 0 var(--ui-space-4);
    font: var(--ui-text-body);
    font-weight: 800;
  }

  .ui-confirm-action {
    border: 0;
    background: var(--ui-accent);
    color: var(--ui-on-accent);
  }

  .ui-confirm-action--danger {
    background: var(--ui-danger);
    color: var(--ui-on-danger);
  }

  .ui-confirm-safe {
    border: 1px solid var(--ui-line);
    background: transparent;
    color: var(--ui-ink);
  }

  .ui-confirm-action:disabled {
    opacity: 0.5;
  }

  .ui-confirm-action:focus-visible,
  .ui-confirm-safe:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }
</style>
